/** Presentation helpers for the External access grid, its drawer, the embedded activity
 *  grid and the activity summary. Labels mirror External_Access_Log's choices; every chip
 *  carries its text (never colour alone). */
import { Chip } from '@mui/material';
import type { ChipProps } from '@mui/material';

import type {
  ExternalAccessChannel,
  ExternalAccessItem,
  ExternalAccessOutcome,
  ExternalAccessPrincipalKind,
} from '@/api/externalAccess';

export const CHANNEL_LABELS: Record<ExternalAccessChannel, string> = {
  api: 'API',
  mcp: 'MCP',
  docs: 'Docs',
  portal: 'Developer portal',
};

export const OUTCOME_LABELS: Record<ExternalAccessOutcome, string> = {
  ok: 'OK',
  client_error: 'Client error',
  server_error: 'Server error',
  denied: 'Denied',
  throttled: 'Throttled',
};

export const PRINCIPAL_LABELS: Record<Exclude<ExternalAccessPrincipalKind, ''>, string> = {
  api_key: 'API key',
  oauth_user: 'OAuth user',
  ventrata_token: 'Ventrata token',
};

/** The DSL values of the enum namespaces — the stored codes, offered as suggestions. */
export const CHANNELS = Object.keys(CHANNEL_LABELS) as ExternalAccessChannel[];
export const OUTCOMES = Object.keys(OUTCOME_LABELS) as ExternalAccessOutcome[];
export const PRINCIPAL_KINDS = Object.keys(PRINCIPAL_LABELS) as Exclude<ExternalAccessPrincipalKind, ''>[];
export const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const OUTCOME_COLORS: Record<ExternalAccessOutcome, ChipProps['color']> = {
  ok: 'success',
  client_error: 'warning',
  server_error: 'error',
  denied: 'error',
  throttled: 'warning',
};

export function OutcomeChip({ outcome }: { outcome: ExternalAccessOutcome }) {
  return (
    <Chip
      size="small"
      color={OUTCOME_COLORS[outcome] ?? 'default'}
      // Denied / throttled are refusals, not failures of the call: outlined sets them apart.
      variant={outcome === 'denied' || outcome === 'throttled' ? 'outlined' : 'filled'}
      label={OUTCOME_LABELS[outcome] ?? outcome}
      sx={{ height: 22, fontSize: 12 }}
    />
  );
}

export function ChannelChip({ channel }: { channel: ExternalAccessChannel }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={CHANNEL_LABELS[channel] ?? channel}
      sx={{ height: 22, fontSize: 12 }}
    />
  );
}

/** The client that made the call: the MCP client name, else the OAuth client id. */
export function clientOf(row: Pick<ExternalAccessItem, 'mcp_client' | 'oauth_client_id'>): string {
  return row.mcp_client || row.oauth_client_id || '';
}

/** A ratio (0–1) as a percentage with one decimal; null → "—". */
export function formatRate(rate: number | null): string {
  if (rate == null) return '—';
  return `${(rate * 100).toFixed(rate > 0 && rate < 0.001 ? 2 : 1)}%`;
}
